import CryptoJS from "crypto-js";

/**
 * Session Key Manager — replaces the user-entered PIN with an
 * auto-generated session key for JWT encryption at rest.
 *
 * The session key is stored in sessionStorage (cleared on tab close).
 * The encrypted JWT is stored in localStorage (persists across refreshes).
 * This provides stronger encryption than a 4-digit PIN (256-bit key vs
 * 10,000 combinations) with zero user friction.
 *
 * Security model:
 *   - Tab open: session key in sessionStorage decrypts JWT from localStorage
 *   - Tab closed: session key is lost, JWT cannot be decrypted → full re-login
 *   - Inactivity: JWT cleared from memory, password re-auth required to resume
 */

const SESSION_KEY_NAME = "blox_session_key";
const ENCRYPTED_TOKEN_KEY = "blox_enc_token";
const ENCRYPTED_REFRESH_KEY = "blox_enc_refresh";
const DECOY_TOKEN_KEY = "blox_enc_decoy";

function generateSessionKey() {
  return CryptoJS.lib.WordArray.random(32).toString();
}

export function storeSessionToken(token, refreshToken) {
  let sessionKey = sessionStorage.getItem(SESSION_KEY_NAME);
  if (!sessionKey) {
    sessionKey = generateSessionKey();
    sessionStorage.setItem(SESSION_KEY_NAME, sessionKey);
  }

  const encryptedToken = CryptoJS.AES.encrypt(token, sessionKey).toString();
  const decoyToken = CryptoJS.AES.encrypt(
    CryptoJS.lib.WordArray.random(24).toString(),
    sessionKey,
  ).toString();

  localStorage.setItem(ENCRYPTED_TOKEN_KEY, encryptedToken);
  localStorage.setItem(DECOY_TOKEN_KEY, decoyToken);

  if (refreshToken) {
    const encryptedRefresh = CryptoJS.AES.encrypt(
      refreshToken,
      sessionKey,
    ).toString();
    localStorage.setItem(ENCRYPTED_REFRESH_KEY, encryptedRefresh);
  }
}

export function retrieveSessionToken() {
  const sessionKey = sessionStorage.getItem(SESSION_KEY_NAME);
  if (!sessionKey) return null;

  const encryptedToken = localStorage.getItem(ENCRYPTED_TOKEN_KEY);
  if (!encryptedToken) return null;

  try {
    const bytes = CryptoJS.AES.decrypt(encryptedToken, sessionKey);
    const token = bytes.toString(CryptoJS.enc.Utf8);
    return token || null;
  } catch {
    return null;
  }
}

export function retrieveRefreshToken() {
  const sessionKey = sessionStorage.getItem(SESSION_KEY_NAME);
  if (!sessionKey) return null;

  const encrypted = localStorage.getItem(ENCRYPTED_REFRESH_KEY);
  if (!encrypted) return null;

  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, sessionKey);
    const token = bytes.toString(CryptoJS.enc.Utf8);
    return token || null;
  } catch {
    return null;
  }
}

export function clearSessionToken() {
  localStorage.removeItem(ENCRYPTED_TOKEN_KEY);
  localStorage.removeItem(ENCRYPTED_REFRESH_KEY);
  localStorage.removeItem(DECOY_TOKEN_KEY);
}

export function hasStoredSession() {
  return (
    sessionStorage.getItem(SESSION_KEY_NAME) !== null &&
    localStorage.getItem(ENCRYPTED_TOKEN_KEY) !== null
  );
}
